const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Kunde inte läsa bilden.'));
    };

    image.src = objectUrl;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

const getExtensionForMime = (mimeType) => {
  if (mimeType === 'image/avif') return 'avif';
  if (mimeType === 'image/webp') return 'webp';
  return null;
};

const getFallbackExtension = (file) => {
  const ext = file.name?.split('.').pop()?.toLowerCase();
  if (ext) return ext;
  if (file.type?.includes('/')) return file.type.split('/')[1];
  return 'bin';
};

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const ALLOWED_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/gif',
]);

export const validateImageFile = (file) => {
  if (!file) return { valid: false, reason: 'Ingen fil vald.' };
  if (!file.type?.startsWith('image/')) return { valid: false, reason: 'Endast bildfiler är tillåtna.' };
  if (!ALLOWED_IMAGE_MIME_TYPES.has(file.type)) {
    return { valid: false, reason: 'Endast JPEG, PNG, WebP, AVIF eller GIF stöds.' };
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return { valid: false, reason: 'Bilden är för stor. Maxstorlek är 8 MB.' };
  }
  return { valid: true, reason: '' };
};

export const optimizeImageForUpload = async (file) => {
  const validation = validateImageFile(file);
  if (!validation.valid) {
    return {
      file,
      extension: getFallbackExtension(file),
      mimeType: file?.type || 'application/octet-stream',
      optimized: false,
    };
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      file,
      extension: getFallbackExtension(file),
      mimeType: file.type,
      optimized: false,
    };
  }

  try {
    const image = await loadImageFromFile(file);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Kunde inte skapa canvas context.');
    const maxWidth = 1600;
    const maxHeight = 1600;
    const baseRatio = Math.min(maxWidth / image.naturalWidth, maxHeight / image.naturalHeight, 1);
    const baseWidth = Math.max(1, Math.round(image.naturalWidth * baseRatio));
    const baseHeight = Math.max(1, Math.round(image.naturalHeight * baseRatio));
    const scaleSteps = [1, 0.85, 0.72];
    const preferredFormats = [
      { mime: 'image/avif', qualities: [0.42, 0.34, 0.28] },
      { mime: 'image/webp', qualities: [0.72, 0.62, 0.55] },
    ];
    let bestCandidate = null;

    for (const scale of scaleSteps) {
      const targetWidth = Math.max(1, Math.round(baseWidth * scale));
      const targetHeight = Math.max(1, Math.round(baseHeight * scale));

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      ctx.clearRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

      for (const format of preferredFormats) {
        const extension = getExtensionForMime(format.mime);
        if (!extension) continue;

        for (const quality of format.qualities) {
          const blob = await canvasToBlob(canvas, format.mime, quality);
          if (!blob || !blob.size) continue;

          if (!bestCandidate || blob.size < bestCandidate.size) {
            bestCandidate = {
              blob,
              extension,
              mimeType: format.mime,
              size: blob.size,
            };
          }
        }
      }
    }

    if (bestCandidate && bestCandidate.size < file.size) {
      const convertedFile = new File(
        [bestCandidate.blob],
        `${Date.now()}.${bestCandidate.extension}`,
        { type: bestCandidate.mimeType }
      );
      return {
        file: convertedFile,
        extension: bestCandidate.extension,
        mimeType: bestCandidate.mimeType,
        optimized: true,
      };
    }
  } catch {
  }

  return {
    file,
    extension: getFallbackExtension(file),
    mimeType: file.type || 'application/octet-stream',
    optimized: false,
  };
};
